// Player stats (POST /v3/live-scores/player-stats), refreshed every minute.
// One poller per event, shared by every MatchCenter showing it: a single
// timer, never two requests in flight, and the timer goes away with the last
// subscriber.

const INTERVAL_MS = 60 * 1000;
// lsport statuses that never change again: finished, cancelled, abandoned.
const FINAL_STATUSES = ['3', '4', '7'];

const pollers = {};

function stop(poller) {
    clearTimeout(poller.timer);
    poller.timer = null;
}

function release(poller) {
    stop(poller);
    if (pollers[poller.eventId] === poller) delete pollers[poller.eventId];
}

function schedule(poller) {
    stop(poller);
    if (poller.done || !poller.listeners.size) return;
    poller.timer = setTimeout(() => run(poller), INTERVAL_MS);
}

async function run(poller) {
    poller.timer = null;
    if (poller.inFlight || !poller.listeners.size) return;
    // Hidden page: skip this round, try again next minute.
    if (typeof document !== 'undefined' && document.hidden) return schedule(poller);
    poller.inFlight = true;
    try {
        const body = await poller.fetcher(poller.eventId);
        const data = body && body.success !== false ? body.data : null;
        if (data && Array.isArray(data.teams)) {
            poller.data = data;
            poller.done = FINAL_STATUSES.includes(String(data.status ?? data.scoreboard?.status ?? ''));
            poller.listeners.forEach(cb => cb(data));
        }
    } catch (e) {
        // Keep the last good answer; the next round retries.
    } finally {
        poller.inFlight = false;
    }
    // Everyone left while the request was out: drop the poller now.
    if (!poller.listeners.size) return release(poller);
    schedule(poller);
}

/**
 * Calls `onData(data)` with the answer now (cached or fetched) and every
 * minute after. `fetcher(eventId)` returns the API body. Returns the
 * unsubscribe function: call it on beforeDestroy / event change.
 */
export function subscribePlayerStats(eventId, fetcher, onData) {
    const key = String(eventId || '');
    if (!key) return () => {};
    let poller = pollers[key];
    if (!poller) {
        poller = pollers[key] = { eventId: key, fetcher, listeners: new Set(), timer: null, inFlight: false, data: null, done: false };
    }
    poller.listeners.add(onData);
    if (poller.data) onData(poller.data);
    // First subscriber (or one after a finished poll): fetch right away.
    if (!poller.timer && !poller.inFlight && !poller.done) run(poller);

    return () => {
        poller.listeners.delete(onData);
        // An answer still on its way releases the poller when it lands, so a
        // new subscriber meanwhile reuses it instead of firing a second call.
        if (!poller.listeners.size && !poller.inFlight) release(poller);
        else if (!poller.listeners.size) stop(poller);
    };
}

// In this match's squad: on the pitch, on the bench, or came on at some point
// (Missing / Predicted players are not).
export const inSquad = p => !!p && (p.hasPlayed === true || p.state === 'Playing' || p.state === 'Benched');
